import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Mail, Lock, User, ArrowRight, Music } from "lucide-react";
import { API } from "@/App";
import SEO, { pageSEO } from "@/components/SEO";
import TurnstileWidget from "@/components/TurnstileWidget";
import useFingerprint from "@/hooks/useFingerprint";

export default function RegisterPage() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    artist_name: ""
  });
  const [loading, setLoading] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);
  const fingerprintVisitorId = useFingerprint();
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (process.env.REACT_APP_TURNSTILE_SITE_KEY && !captchaToken) {
      toast.error("Please complete the CAPTCHA challenge below");
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API}/auth/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...formData,
          turnstile_token: captchaToken,
          fingerprint_visitor_id: fingerprintVisitorId,
        }),
        credentials: "include"
      });

      if (response.ok) {
        const data = await response.json();
        toast.success("Account created! Check your email to verify.", { duration: 8000 });
        navigate("/dashboard", { state: { user: data.user } });
      } else {
        const error = await response.json();
        toast.error(error.detail || "Registration failed");
      }
    } catch (error) {
      toast.error("Connection error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // SYCE Google OAuth signup
  const handleGoogleSignup = async () => {
    const origin = window.location.origin;
    const url = `${API}/auth/google/login?redirect_origin=${encodeURIComponent(origin)}`;
    
    for (let attempt = 1; attempt <= 2; attempt++) {
      try {
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 10000);
        
        const response = await fetch(url, { signal: controller.signal });
        clearTimeout(timeout);
        
        if (response.ok) {
          const data = await response.json();
          window.location.href = data.auth_url;
          return;
        }
        
        if (attempt < 2) continue;
        toast.error("Failed to initialize Google signup. Please try email signup.");
      } catch (error) {
        if (attempt < 2) continue;
        toast.error("Connection error. Please check your internet and try again.");
      }
    }
  };

  return (
    <div className="min-h-screen bg-background flex">
      <SEO {...pageSEO.register} />
      
      {/* Left side - Image */}
      <div className="hidden lg:flex flex-1 relative">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(https://images.unsplash.com/photo-1736296920783-98461683653a?w=1200&q=80)` }}
        />
        <div className="absolute inset-0 bg-gradient-to-l from-background to-transparent" />
        <div className="absolute inset-0 bg-primary/10" />
        <div className="absolute bottom-12 left-12 max-w-md">
          <h2 className="font-heading text-4xl font-bold mb-4">Join the Movement</h2>
          <p className="text-lg text-muted-foreground">
            Start distributing your music to 150+ platforms today. Keep up to 100% of your royalties.
          </p>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <Link to="/" className="flex items-center gap-2 mb-12">
            <img 
              src="https://customer-assets.emergentagent.com/job_royalty-revolution/artifacts/gc921mlg_IMG_1214.png" 
              alt="SYCE.me" 
              className="h-12 w-auto"
            />
          </Link>

          <h1 className="font-heading text-3xl font-bold mb-2" data-testid="register-title">Create your account</h1>
          <p className="text-muted-foreground mb-8">
            Already have an account?{" "}
            <Link to="/login" className="text-primary hover:underline" data-testid="login-link">Sign in</Link>
          </p>

          {/* Google Signup */}
          <Button
            type="button"
            variant="outline"
            className="w-full h-12 mb-6 border-border hover:bg-white/5"
            onClick={handleGoogleSignup}
            data-testid="google-signup-btn"
          >
            <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
              <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continue with Google
          </Button>

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-border"></div>
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-background px-2 text-muted-foreground">Or continue with email</span>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Full Name</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    placeholder="John Doe"
                    value={formData.name}
                    onChange={handleChange}
                    className="pl-10 h-12 bg-input/50"
                    required
                    data-testid="name-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="artist_name">Artist Name</Label>
                <div className="relative">
                  <Music className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    id="artist_name"
                    name="artist_name"
                    type="text"
                    placeholder="Stage Name"
                    value={formData.artist_name}
                    onChange={handleChange}
                    className="pl-10 h-12 bg-input/50"
                    data-testid="artist-name-input"
                  />
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="artist@example.com"
                  value={formData.email}
                  onChange={handleChange}
                  className="pl-10 h-12 bg-input/50"
                  required
                  data-testid="email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  id="password"
                  name="password"
                  type="password"
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={handleChange}
                  className="pl-10 h-12 bg-input/50"
                  required
                  minLength={6}
                  data-testid="password-input"
                />
              </div>
              <p className="text-xs text-muted-foreground">Minimum 6 characters</p>
            </div>

            <div className="flex items-start gap-2">
              <input type="checkbox" className="mt-1 rounded border-border" required />
              <span className="text-sm text-muted-foreground">
                I agree to the{" "}
                <a href="#" className="text-primary hover:underline">Terms of Service</a>
                {" "}and{" "}
                <a href="#" className="text-primary hover:underline">Privacy Policy</a>
              </span>
            </div>

            <TurnstileWidget onVerify={setCaptchaToken} />

            <Button
              type="submit"
              className="w-full h-12 bg-primary text-primary-foreground hover:bg-primary/90 rounded-full font-bold"
              disabled={loading}
              data-testid="register-submit-btn"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <span className="animate-spin w-4 h-4 border-2 border-primary-foreground border-t-transparent rounded-full"></span>
                  Creating account...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  Create Account
                  <ArrowRight className="w-4 h-4" />
                </span>
              )}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
